import * as React from 'react';
import {FunctionComponent, useMemo, useRef} from 'react';
import {VictoryAxis, VictoryChart, VictoryLine, VictoryVoronoiContainer} from 'victory';
import {DomainPropType} from 'victory-core';
import {axisDataFromGraphValues} from '../Support/axisDataFromGraphValues';
import {normalizeGraphValues} from '../Support/normalizeGraphValues';

interface OwnProps {
    height: number;
    padding?: {
        top?: number;
        bottom?: number;
        left?: number;
        right?: number;
    };
    formatX: (x: number) => string;
    formatY: (y: number) => string;
    sets: Array<{
        color?: string;
        values: Array<{x: number; y: number; label?: string}>;
    }>;
}

const styles = {
    axis: {
        axis: {
            stroke: 'rgba(0, 0, 0, 0.1)',
        },
        grid: {
            stroke: 'rgba(0, 0, 0, 0.1)',
            strokeWidth: 1,
            strokeDasharray: 4,
        },
        tickLabels: {
            fill: '#000',
            fontSize: 8,
            padding: 0,
        },
    },
};

function reduceDataPoints(map: Array<{x: number; y: number}>): Array<{x: number; y: number}> {
    const epsilon = 0.01;

    //Recursive Ramer–Douglas–Peucker algorithm
    function perpendicularDistance(
        point: {x: number; y: number},
        lineStart: {x: number; y: number},
        lineEnd: {
            x: number;
            y: number;
        },
    ) {
        let dx = lineEnd.x - lineStart.x;
        let dy = lineEnd.y - lineStart.y;

        //Normalise
        const mag = Math.sqrt(dx * dx + dy * dy);
        if (mag > 0.0) {
            dx /= mag;
            dy /= mag;
        }

        const pvx = point.x - lineStart.x;
        const pvy = point.y - lineStart.y;

        //Get dot product (project pv onto normalized direction)
        const pvdot = dx * pvx + dy * pvy;

        //Scale line direction vector and subtract it from pv
        const ax = pvx - pvdot * dx;
        const ay = pvy - pvdot * dy;

        return Math.sqrt(ax * ax + ay * ay);
    }

    //Find the point with the maximum distance
    let dmax = 0;
    let index = 0;
    const end = map.length - 1;
    for (let i = 1; i < end; ++i) {
        const d = perpendicularDistance(map[i], map[0], map[end]);
        if (d > dmax) {
            index = i;
            dmax = d;
        }
    }

    //If max distance is greater than epsilon, recursively simplify
    if (dmax > epsilon) {
        //Recursive call
        const recResults1 = reduceDataPoints(map.slice(0, index + 1));
        const recResults2 = reduceDataPoints(map.slice(index));

        //Build the result list
        return recResults1.slice(0, recResults1.length - 1).concat(recResults2);
    } else {
        return [map[0], map[end]];
    }
}

export const PlainMultiGraph: FunctionComponent<OwnProps> = ({height, padding, formatX, formatY, sets}) => {
    const ref = useRef<HTMLElement>(null);

    const setsWithReducedDataPoints = useMemo(() => {
        return sets
            .filter((set) => set.values.length > 1)
            .map((set) => {
                return {
                    ...set,
                    values: normalizeGraphValues(set.values),
                };
            })
            .map((set) => {
                const map = set.values.map((value) => {
                    return {
                        x: value.x,
                        y: value.y,
                    };
                });
                if (map.length < 800) {
                    return set;
                }

                return {
                    color: set.color,
                    values: reduceDataPoints(map),
                };
            });
    }, [sets]);

    const domain: DomainPropType = useMemo(() => {
        const values = sets.reduce((acc: Array<{x: number; y: number; label?: string}>, set) => {
            return [...acc, ...set.values];
        }, []);
        return {
            x: [Math.min(...values.map((value) => value.x)), Math.max(...values.map((value) => value.x))],
        };
    }, [sets]);

    const axis = useMemo(() => {
        const values = sets.reduce((acc: Array<{x: number; y: number; label?: string}>, set) => {
            return [...acc, ...set.values];
        }, []);

        return axisDataFromGraphValues(values, formatX, formatY);
    }, [sets]);

    return (
        <>
            <VictoryChart height={height} padding={padding} animate={false} domain={domain}>
                {axis?.x ? (
                    <VictoryAxis
                        fixLabelOverlap
                        style={styles.axis}
                        tickValues={axis.x.map((tick) => tick.value)}
                        tickFormat={(value) => {
                            return axis?.x?.find((tick) => tick.value === value)?.label ?? '';
                        }}
                    />
                ) : (
                    <VictoryAxis
                        style={{
                            axis: {stroke: 'transparent'},
                            ticks: {stroke: 'transparent'},
                            tickLabels: {fill: 'transparent'},
                        }}
                    />
                )}
                {axis?.y ? (
                    <VictoryAxis
                        style={styles.axis}
                        dependentAxis
                        fixLabelOverlap
                        orientation="left"
                        tickValues={axis.y.map((tick) => tick.value)}
                        tickFormat={(value: any) => {
                            return axis?.y?.find((tick) => tick.value === value)?.label ?? '';
                        }}
                    />
                ) : (
                    <VictoryAxis
                        style={{
                            axis: {stroke: 'transparent'},
                            ticks: {stroke: 'transparent'},
                            tickLabels: {fill: 'transparent'},
                        }}
                    />
                )}
                {setsWithReducedDataPoints.map((set, index) => {
                    return (
                        <VictoryLine
                            key={'line-' + index}
                            name="line"
                            data={set.values}
                            style={{
                                data: {
                                    stroke: set.color,
                                    strokeWidth: 1,
                                },
                            }}
                        />
                    );
                })}
            </VictoryChart>
        </>
    );
};
